import React from 'react';
import { clsx } from 'clsx';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'destructive' | 'solid';
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    loading?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    fullWidth?: boolean;
    asChild?: boolean;
    children: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({
        variant = 'primary',
        size = 'md',
        loading = false,
        leftIcon,
        rightIcon,
        fullWidth = false,
        asChild = false,
        className,
        disabled,
        children,
        ...props
    }, ref) => {
        const baseClasses = [
            'inline-flex items-center justify-center font-medium transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-offset-2',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
            'rounded-lg border'
        ];

        const variantClasses = {
            primary: [
                'bg-primary-600 border-primary-600 text-white',
                'hover:bg-primary-700 hover:border-primary-700',
                'focus:ring-primary-500',
                'active:bg-primary-800'
            ],
            secondary: [
                'bg-gray-600 border-gray-600 text-white',
                'hover:bg-gray-700 hover:border-gray-700',
                'focus:ring-gray-500',
                'active:bg-gray-800'
            ],
            solid: [
                'bg-slate-900 border-slate-900 text-white',
                'hover:bg-slate-800 hover:border-slate-800',
                'focus:ring-slate-500',
                'active:bg-slate-700',
                'shadow-sm hover:shadow-md',
                'font-semibold'
            ],
            outline: [
                'bg-transparent border-2 border-gray-300 text-gray-700',
                'hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900',
                'focus:ring-primary-500 focus:border-primary-500',
                'active:bg-gray-100 active:border-gray-500',
                'transition-colors duration-200'
            ],
            ghost: [
                'bg-transparent border-transparent text-gray-700',
                'hover:bg-gray-100',
                'focus:ring-primary-500',
                'active:bg-gray-200'
            ],
            danger: [
                'bg-red-600 border-red-600 text-white',
                'hover:bg-red-700 hover:border-red-700',
                'focus:ring-red-500',
                'active:bg-red-800'
            ],
            success: [
                'bg-green-600 border-green-600 text-white',
                'hover:bg-green-700 hover:border-green-700',
                'focus:ring-green-500',
                'active:bg-green-800'
            ],
            destructive: [
                'bg-red-600 border-red-600 text-white',
                'hover:bg-red-700 hover:border-red-700',
                'focus:ring-red-500',
                'active:bg-red-800',
                'shadow-sm hover:shadow-md',
                'font-semibold'
            ]
        };

        const sizeClasses = {
            xs: 'px-2 py-1 text-xs min-h-6',
            sm: 'px-3 py-1.5 text-sm min-h-8',
            md: 'px-4 py-2 text-sm min-h-10',
            lg: 'px-6 py-3 text-base min-h-12',
            xl: 'px-8 py-4 text-lg min-h-14'
        };

        const iconSizeClasses = {
            xs: 'w-3 h-3',
            sm: 'w-4 h-4',
            md: 'w-4 h-4',
            lg: 'w-5 h-5',
            xl: 'w-6 h-6'
        };

        const classes = clsx(
            baseClasses,
            variantClasses[variant],
            sizeClasses[size],
            fullWidth && 'w-full',
            className
        );

        const iconSize = iconSizeClasses[size];

        // Función para clonar íconos con las clases correctas
        const cloneIconWithSize = (icon: React.ReactNode) => {
            if (React.isValidElement(icon)) {
                return React.cloneElement(icon, {
                    className: clsx(iconSize, icon.props.className)
                } as any);
            }
            return icon;
        };

        // Contenido del botón
        const buttonContent = (
            <>
                {loading && (
                    <Loader2 className={clsx('animate-spin mr-2', iconSize)} />
                )}

                {!loading && leftIcon && (
                    <span className="mr-2 flex items-center">
                        {cloneIconWithSize(leftIcon)}
                    </span>
                )}

                <span className={loading ? 'opacity-70' : ''}>
                    {children}
                </span>

                {!loading && rightIcon && (
                    <span className="ml-2 flex items-center">
                        {cloneIconWithSize(rightIcon)}
                    </span>
                )}
            </>
        );

        // Si asChild es true, aplicamos las clases al primer hijo
        if (asChild) {
            const child = React.Children.only(children) as React.ReactElement;
            return React.cloneElement(child, {
                className: clsx(classes, child.props?.className),
                ref,
                ...props,
                children: (
                    <>
                        {loading && (
                            <Loader2 className={clsx('animate-spin mr-2', iconSize)} />
                        )}

                        {!loading && leftIcon && (
                            <span className="mr-2 flex items-center">
                                {cloneIconWithSize(leftIcon)}
                            </span>
                        )}

                        <span className={loading ? 'opacity-70' : ''}>
                            {child.props?.children || child}
                        </span>

                        {!loading && rightIcon && (
                            <span className="ml-2 flex items-center">
                                {cloneIconWithSize(rightIcon)}
                            </span>
                        )}
                    </>
                )
            });
        }

        return (
            <button
                ref={ref}
                className={classes}
                disabled={disabled || loading}
                {...props}
            >
                {buttonContent}
            </button>
        );
    }
);

Button.displayName = 'Button';

export default Button;